import React, {FC, useId, useState, useMemo} from 'react'
import {ErrorBoundary} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'
import {RouteComponentProps} from 'react-router-dom'
import {FormModelType, useForm} from 'reactjs-use-form'

import DoneIcon from '@mui/icons-material/Done'
import EditIcon from '@mui/icons-material/Edit'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Theme,
  Typography,
} from '@mui/material'
import {
  PGButton,
  getProductLineColors,
  PGTheme,
} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'
import * as Yup from 'yup'

import {ErrorBoundaryFallback} from '@progyny/components/ErrorBoundary'
import PageLoader from '@progyny/components/PageLoader'
import {
  MeDocument,
  useAllCountriesQuery,
  useResendPrivateEmailConfirmationMutation,
  useUpdatePersonaInformationMutation,
} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useProductLine} from '@progyny/hooks/useProductLine'
import MemberFile from '@progyny/pages/Profile/MemberFile/MemberFile'
import {SubpageHeader} from '@progyny/pages/Profile/fragments/header'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'
import {FERTILITY, MENOPAUSE} from '@progyny/utils/constants'
import {REACT_APP_DOMAIN_NAME} from '@progyny/utils/env'

import {theme} from '@root/theme'

const useStyles = makeStyles()((theme: Theme) => ({
  message: {
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  emailInput: {
    marginTop: theme.spacing(3),
  },

  privateEmailAlert: {
    marginTop: theme.spacing(3),
  },
  sectionCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    border: '1px solid #D6D8E0',
    borderRadius: 8,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  viewFieldRow: {
    display: 'flex',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
  },
  viewFieldHalf: {
    flex: 1,
  },
  editButton: {
    color: theme.palette.primary.main,
    textTransform: 'none',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      '& .MuiButtonBase-root': {
        width: '100%',
      },
    },
  },
  viewField: {
    marginBottom: theme.spacing(1.5),
  },
  viewLabel: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  viewValue: {
    fontSize: '16px',
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
}))

type EditMode = 'account' | 'address' | 'email' | null

interface PersonalInfoProps {}

const PersonalInfoPage: FC<RouteComponentProps<PersonalInfoProps>> = () => {
  const [update, {loading}] = useUpdatePersonaInformationMutation()
  const [resend, {loading: resendLoading}] =
    useResendPrivateEmailConfirmationMutation()
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const {classes} = useStyles()
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const getErrorMessage = useErrorCodes()
  const user = useMeStore((s) => s.user)
  const {data} = useAllCountriesQuery()
  const {goTo} = useRoutes()
  const privateEmailAddressLabelId = useId()

  const [editMode, setEditMode] = useState<EditMode>(null)
  const [formKey, setFormKey] = useState(0)

  const emailValidation = Yup.string().email().nullable()

  // Account details form model
  const accountFormModel: FormModelType = useMemo(
    () => ({
      preferredGivenName: {
        value: user?.member?.preferredGivenName || '',
        required: false,
      },
      preferredSurname: {
        value: user?.member?.preferredSurname || '',
        required: false,
      },
    }),
    [user?.member?.preferredGivenName, user?.member?.preferredSurname]
  )

  // Address form model
  const addressFormModel: FormModelType = useMemo(
    () => ({
      addressStreetAddress: {
        value: user?.member?.privateAddress?.streetAddress || '',
        required: true,
      },
      addressCity: {
        value: user?.member?.privateAddress?.city || user?.member?.city || '',
        required: true,
      },
      addressPostcode: {
        value: user?.member?.privateAddress?.postcode || '',
        required: true,
      },
      addressCountry: {
        value:
          user?.member?.privateAddress?.country.code ||
          user?.member.countryOfEmployment?.country.code ||
          '',
        required: true,
      },
    }),
    [
      user?.member?.privateAddress,
      user?.member?.city,
      user?.member?.countryOfEmployment,
    ]
  )

  // Email form model
  const emailFormModel: FormModelType = useMemo(
    () => ({
      privateEmail: {
        value:
          user?.member?.newPrivateEmail === ''
            ? user?.member?.privateEmail || ''
            : user?.member?.newPrivateEmail || '',
        required: false,
        validator: (privateEmail) => {
          if (privateEmail && !emailValidation.isValidSync(privateEmail)) {
            return t('profile_personal_info_please_enter_valid_email')
          } else return ''
        },
      },
    }),
    [
      user?.member?.privateEmail,
      user?.member?.newPrivateEmail,
      t,
      emailValidation,
    ]
  )

  const {
    values: accountValues,
    isDirty: accountIsDirty,
    handleOnChange: handleAccountChange,
  } = useForm(accountFormModel, () => {})

  const {
    values: addressValues,
    errors: addressErrors,
    isDirty: addressIsDirty,
    handleOnChange: handleAddressChange,
  } = useForm(addressFormModel, () => {})

  const {
    values: emailValues,
    errors: emailErrors,
    isDirty: emailIsDirty,
    handleOnChange: handleEmailChange,
  } = useForm(emailFormModel, () => {})

  const handleSaveAccount = () => {
    update({
      variables: {
        preferredGivenName: accountValues.preferredGivenName,
        preferredSurname: accountValues.preferredSurname,
      },
      refetchQueries: [{query: MeDocument}],
      awaitRefetchQueries: true,
    })
      .then((res) => {
        if (res.data?.updatePersonalInformation?.success) {
          setEditMode(null)
          enqueueSnackbar({
            message: t('profile_personal_info_changes_saved'),
            variant: 'success',
          })
        } else if (res.data?.updatePersonalInformation?.errors) {
          const errors = res.data?.updatePersonalInformation?.errors
          const message = errors[0]?.messages[0]
          enqueueSnackbar({message: message, variant: 'error'})
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const handleSaveAddress = () => {
    update({
      variables: {
        addressStreetAddress: addressValues.addressStreetAddress,
        addressCity: addressValues.addressCity,
        addressPostcode: addressValues.addressPostcode,
        addressCountry: addressValues.addressCountry,
      },
      refetchQueries: [{query: MeDocument}],
      awaitRefetchQueries: true,
    })
      .then((res) => {
        if (res.data?.updatePersonalInformation?.success) {
          setEditMode(null)
          enqueueSnackbar({
            message: t('profile_personal_info_changes_saved'),
            variant: 'success',
          })
        } else if (res.data?.updatePersonalInformation?.errors) {
          const errors = res.data?.updatePersonalInformation?.errors
          const message = errors[0]?.messages[0]
          enqueueSnackbar({message: message, variant: 'error'})
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const handleSaveEmail = () => {
    update({
      variables: {
        privateEmail: emailValues.privateEmail || null,
      },
      refetchQueries: [{query: MeDocument}],
      awaitRefetchQueries: true,
    })
      .then((res) => {
        if (res.data?.updatePersonalInformation?.success) {
          setEditMode(null)
          enqueueSnackbar({
            message: t('profile_personal_info_changes_saved'),
            variant: 'success',
          })
        } else if (res.data?.updatePersonalInformation?.errors) {
          const errors = res.data?.updatePersonalInformation?.errors
          const message = errors[0]?.messages[0]
          enqueueSnackbar({message: message, variant: 'error'})
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const handleCancel = () => {
    setFormKey((prev) => prev + 1)
    setEditMode(null)
  }

  const handleResendConfirmation = () => {
    resend()
      .then((resendResult) => {
        const data = resendResult.data!.resendPrivateEmailConfirmation
        const success = Boolean(data?.success)
        const errorCode = data!.errorCode as string
        if (!success) {
          enqueueSnackbar({
            message: getErrorMessage(errorCode),
            variant: 'error',
          })
          if (errorCode === 'user-not-logged-in') {
            goTo(RouteEnum.LOGIN)
          }
        } else {
          enqueueSnackbar({
            message: t('profile_personal_info_confirmation_email_sent'),
            variant: 'success',
          })
        }
      })
      .catch((error) => {
        if (error.networkError && error.networkError.statusCode === 429) {
          enqueueSnackbar({
            message: t('too_many_requests'),
            variant: 'warning',
          })
        } else {
          enqueueSnackbar({
            message: t(
              'home_confirmation_box_errors_unable_to_resend_confirmation'
            ),
            variant: 'error',
          })
        }
      })
  }

  if (!user) return <PageLoader position={'center'} />

  const getCountryName = (code: string) => {
    return data?.allCountries.find((c) => c?.code === code)?.name || code
  }

  return (
    <Box
      mt={{xs: 10, md: 15}}
      paddingX={{xs: 2, md: 3}}
      paddingBottom={'100px'}
    >
      <ErrorBoundary fallbackRender={ErrorBoundaryFallback}>
        <SubpageHeader
          title={t('profile_personal_information')}
          memberId={user?.member?.incidentId}
        />
        <Grid container mt={2} maxWidth={840}>
          {/* ID Document Section */}
          {user?.member?.identityDocumentUrl && (
            <Grid item xs={12}>
              <MemberFile />
            </Grid>
          )}
          {/* Account Details Section */}
          <Grid item xs={12}>
            <Box className={classes.sectionHeader} mb={2}>
              <Typography variant={'h6'} fontWeight={600}>
                {t('profile_personal_info_account_details')}
              </Typography>
              {editMode !== 'account' && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode('account')}
                  className={classes.editButton}
                >
                  {t('common_edit')}
                </Button>
              )}
            </Box>
            <Card className={classes.sectionCard}>
              {editMode === 'account' ? (
                <>
                  <Grid container spacing={2}>
                    <Grid xs={12} sm={6} item>
                      <TextField
                        size={'small'}
                        name='firstName'
                        label={t('profile_personal_info_recipient_full_name')}
                        variant={'outlined'}
                        margin={'dense'}
                        autoComplete='fname'
                        fullWidth
                        value={`${user.firstName} ${user.lastName}`}
                        disabled
                      />
                    </Grid>
                    <Grid
                      xs={12}
                      sm={6}
                      item
                      sx={{display: {xs: 'none', sm: 'block'}}}
                    />
                    <Grid xs={12} sm={6} item>
                      <TextField
                        key={`account-given-name-${formKey}`}
                        size={'small'}
                        name='preferredGivenName'
                        label={t('profile_personal_info_preferred_first_name')}
                        variant={'outlined'}
                        margin={'dense'}
                        autoComplete='given-name'
                        fullWidth
                        value={accountValues.preferredGivenName}
                        onChange={handleAccountChange}
                      />
                    </Grid>
                    <Grid xs={12} sm={6} item>
                      <TextField
                        key={`account-surname-${formKey}`}
                        size={'small'}
                        name='preferredSurname'
                        label={t('profile_personal_info_preferred_last_name')}
                        variant={'outlined'}
                        margin={'dense'}
                        autoComplete='family-name'
                        fullWidth
                        value={accountValues.preferredSurname}
                        onChange={handleAccountChange}
                      />
                    </Grid>
                  </Grid>

                  {user?.isManager && (
                    <Alert
                      severity='info'
                      classes={{message: classes.message}}
                      sx={{mt: 2}}
                    >
                      <Typography variant={'caption'}>
                        {t('profile_personal_info_name_dashboard_notice')}
                      </Typography>
                    </Alert>
                  )}

                  <Box className={classes.actionButtons}>
                    <Button onClick={handleCancel} variant='outlined'>
                      {t('common_cancel')}
                    </Button>
                    <PGButton
                      variant='contained'
                      disabled={!accountIsDirty || loading}
                      onClick={handleSaveAccount}
                      endIcon={<DoneIcon />}
                      label={t('common_save')}
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Box className={classes.viewField}>
                    <Typography className={classes.viewLabel}>
                      {t('profile_personal_info_recipient_full_name')}
                    </Typography>
                    <Typography className={classes.viewValue}>
                      {`${user.firstName} ${user.lastName}`}
                    </Typography>
                  </Box>
                  <Box className={classes.viewFieldRow}>
                    <Box className={classes.viewFieldHalf}>
                      <Typography className={classes.viewLabel}>
                        {t('profile_personal_info_preferred_first_name')}
                      </Typography>
                      <Typography className={classes.viewValue}>
                        {user?.member?.preferredGivenName || '-'}
                      </Typography>
                    </Box>
                    <Box className={classes.viewFieldHalf}>
                      <Typography className={classes.viewLabel}>
                        {t('profile_personal_info_preferred_last_name')}
                      </Typography>
                      <Typography className={classes.viewValue}>
                        {user?.member?.preferredSurname || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Card>
          </Grid>

          {/* Address Section */}
          <Grid item xs={12}>
            <Box className={classes.sectionHeader} mb={2}>
              <Typography variant={'h6'} fontWeight={600}>
                {t('profile_personal_info_address_section')}
              </Typography>
              {editMode !== 'address' && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode('address')}
                  className={classes.editButton}
                >
                  {t('common_edit')}
                </Button>
              )}
            </Box>
            <Card className={classes.sectionCard}>
              {editMode === 'address' ? (
                <>
                  <Typography variant='subtitle2' sx={{mb: 2}}>
                    {t('profile_personal-info_primary_residence')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid xs={12} item>
                      <TextField
                        key={`address-street-${formKey}`}
                        required
                        size={'small'}
                        name='addressStreetAddress'
                        label={t('profile_personal_info_home_address')}
                        variant={'outlined'}
                        margin={'dense'}
                        multiline={true}
                        fullWidth={true}
                        value={addressValues.addressStreetAddress}
                        autoComplete={'street-address'}
                        onChange={handleAddressChange}
                        error={addressErrors.addressStreetAddress.hasError}
                        helperText={t(
                          addressErrors.addressStreetAddress.message
                        )}
                      />
                    </Grid>

                    <Grid xs={12} sm={6} item>
                      <TextField
                        key={`address-city-${formKey}`}
                        required
                        size={'small'}
                        inputProps={{'aria-label': 'City'}}
                        name='addressCity'
                        label={t('common_city')}
                        variant={'outlined'}
                        margin={'dense'}
                        fullWidth
                        autoComplete={'address-level2'}
                        value={addressValues.addressCity}
                        onChange={handleAddressChange}
                        error={addressErrors.addressCity.hasError}
                        helperText={t(addressErrors.addressCity.message)}
                      />
                    </Grid>

                    <Grid xs={12} sm={6} item>
                      <FormControl
                        key={`address-country-${formKey}`}
                        variant={'outlined'}
                        size='small'
                        fullWidth
                        required
                      >
                        <InputLabel id='country-label'>
                          {t('common_country')}
                        </InputLabel>
                        <Select
                          label={t('common_country')}
                          labelId={'country-label'}
                          id={'country-select'}
                          placeholder={t('common_country')}
                          name={'addressCountry'}
                          autoComplete={'country'}
                          value={addressValues.addressCountry}
                          onChange={handleAddressChange}
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                          fullWidth
                        >
                          {data?.allCountries.map((c) => (
                            <MenuItem key={c?.code} value={c?.code}>
                              {c?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid xs={12} sm={6} item>
                      <TextField
                        key={`address-postcode-${formKey}`}
                        required
                        size={'small'}
                        name='addressPostcode'
                        label={t('common_postal_code')}
                        variant={'outlined'}
                        fullWidth
                        autoComplete={'postal-code'}
                        value={addressValues.addressPostcode}
                        onChange={handleAddressChange}
                        error={addressErrors.addressPostcode.hasError}
                        helperText={t(addressErrors.addressPostcode.message)}
                      />
                    </Grid>
                  </Grid>

                  <Box className={classes.actionButtons}>
                    <Button onClick={handleCancel} variant='outlined'>
                      {t('common_cancel')}
                    </Button>
                    <PGButton
                      variant='contained'
                      disabled={!addressIsDirty || loading}
                      onClick={handleSaveAddress}
                      endIcon={<DoneIcon />}
                      label={t('common_save')}
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant='subtitle2' sx={{mb: 2}}>
                    {t('profile_personal-info_primary_residence')}
                  </Typography>
                  <Box className={classes.viewField}>
                    <Typography className={classes.viewLabel}>
                      {t('profile_personal_info_home_address')}
                    </Typography>
                    <Typography className={classes.viewValue}>
                      {user?.member?.privateAddress?.streetAddress || '-'}
                    </Typography>
                  </Box>
                  <Box className={classes.viewFieldRow}>
                    <Box className={classes.viewFieldHalf}>
                      <Typography className={classes.viewLabel}>
                        {t('common_city')}
                      </Typography>
                      <Typography className={classes.viewValue}>
                        {user?.member?.privateAddress?.city ||
                          user?.member?.city ||
                          '-'}
                      </Typography>
                    </Box>
                    <Box className={classes.viewFieldHalf}>
                      <Typography className={classes.viewLabel}>
                        {t('common_country')}
                      </Typography>
                      <Typography className={classes.viewValue}>
                        {user?.member?.privateAddress?.country?.code
                          ? getCountryName(
                              user.member.privateAddress.country.code
                            )
                          : user?.member?.countryOfEmployment?.country?.code
                            ? getCountryName(
                                user.member.countryOfEmployment.country.code
                              )
                            : '-'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={classes.viewField}>
                    <Typography className={classes.viewLabel}>
                      {t('common_postal_code')}
                    </Typography>
                    <Typography className={classes.viewValue}>
                      {user?.member?.privateAddress?.postcode || '-'}
                    </Typography>
                  </Box>
                </>
              )}
            </Card>
          </Grid>

          {/* Email Details Section */}
          <Grid item xs={12}>
            <Box className={classes.sectionHeader} mb={2}>
              <Typography variant={'h6'} fontWeight={600}>
                {t('profile_personal_info_email_addresses')}
              </Typography>
              {editMode !== 'email' && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode('email')}
                  className={classes.editButton}
                >
                  {t('common_edit')}
                </Button>
              )}
            </Box>
            <Card className={classes.sectionCard}>
              <Box display='flex' flexDirection='column' gap={3}>
                {/* Work Email */}
                <Box>
                  <Typography variant='subtitle1'>
                    {t('profile_personal_info_work_email_heading')}
                  </Typography>
                  <Typography variant='body2' sx={{mb: 2}}>
                    {t('profile_personal_info_work_email_description')}
                  </Typography>
                  <TextField
                    size={'small'}
                    label={t('profile_personal_info_work_email_label')}
                    variant={'outlined'}
                    margin={'dense'}
                    fullWidth
                    disabled
                    value={user?.email || ''}
                    className={classes.emailInput}
                  />
                </Box>

                {/* Private Email */}
                <Box>
                  <Typography
                    variant='subtitle1'
                    gutterBottom
                    id={privateEmailAddressLabelId}
                  >
                    {t('profile_personal_info_private_email_heading')}
                  </Typography>
                  <Typography variant='body2' sx={{mb: 2}}>
                    {t('profile_personal_info_private_email_label')}
                  </Typography>

                  {editMode === 'email' ? (
                    <>
                      <TextField
                        key={`email-private-${formKey}`}
                        size={'small'}
                        name='privateEmail'
                        label={t(
                          'profile_personal_info_private_email_field_label'
                        )}
                        placeholder={t(
                          'profile_personal_info_private_email_placeholder'
                        )}
                        InputLabelProps={{shrink: true}}
                        variant={'outlined'}
                        margin={'dense'}
                        fullWidth
                        value={emailValues.privateEmail}
                        onChange={handleEmailChange}
                        className={classes.emailInput}
                        error={emailErrors.privateEmail.hasError}
                        helperText={emailErrors.privateEmail.message}
                      />
                      <Box className={classes.actionButtons}>
                        <Button onClick={handleCancel} variant='outlined'>
                          {t('common_cancel')}
                        </Button>
                        <PGButton
                          variant='contained'
                          disabled={!emailIsDirty || loading}
                          onClick={handleSaveEmail}
                          endIcon={<DoneIcon />}
                          label={t('common_save')}
                        />
                      </Box>
                    </>
                  ) : (
                    <>
                      <TextField
                        size={'small'}
                        label={t(
                          'profile_personal_info_private_email_field_label'
                        )}
                        variant={'outlined'}
                        margin={'dense'}
                        fullWidth
                        disabled
                        InputLabelProps={{shrink: true}}
                        className={classes.emailInput}
                        value={
                          user?.member?.newPrivateEmail === ''
                            ? user?.member?.privateEmail ||
                              t(
                                'profile_personal_info_private_email_placeholder'
                              )
                            : user?.member?.newPrivateEmail ||
                              t(
                                'profile_personal_info_private_email_placeholder'
                              )
                        }
                      />
                      {!user?.member?.privateEmailConfirmed &&
                        user?.member?.privateEmail &&
                        !user?.member?.privateEmailChangePending && (
                          <Alert
                            severity='warning'
                            className={classes.privateEmailAlert}
                          >
                            <Typography variant={'caption'}>
                              {t(
                                'profile_personal_info_private_email_not_verified'
                              )}
                            </Typography>
                            {resendLoading ? (
                              <Typography variant={'body1'}>
                                {t('profile_personal_info_resending')}
                              </Typography>
                            ) : (
                              <Link
                                variant={'caption'}
                                display='inline-block'
                                width={'fit-content'}
                                onClick={handleResendConfirmation}
                              >
                                {t(
                                  'profile_personal_info_resend_confirmation_email'
                                )}
                              </Link>
                            )}
                          </Alert>
                        )}
                      {user?.member?.privateEmail &&
                        user?.member?.privateEmailChangePending && (
                          <Alert
                            severity='warning'
                            className={classes.privateEmailAlert}
                          >
                            <Typography variant={'caption'}>
                              {t(
                                'profile_personal_info_private_email_use_work'
                              )}
                            </Typography>
                            <Typography variant='subtitle1'>
                              {user?.member?.privateEmail}
                            </Typography>
                            {resendLoading ? (
                              <Typography variant={'body1'}>
                                {t('profile_personal_info_resending')}
                              </Typography>
                            ) : (
                              <Link
                                variant={'caption'}
                                display='inline-block'
                                width={'fit-content'}
                                onClick={handleResendConfirmation}
                              >
                                {t(
                                  'profile_personal_info_resend_confirmation_email'
                                )}
                              </Link>
                            )}
                          </Alert>
                        )}
                    </>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Delete Account Section */}
          <Grid item xs={12}>
            <Box mt={4}>
              <Alert
                severity='info'
                sx={{
                  borderRadius: '12px',
                  backgroundColor:
                    productLine === FERTILITY ? colors.secondary : '#FFDEE040',
                  '& .MuiAlert-icon': {
                    padding: '10px 0',
                    '& .MuiSvgIcon-root': {fill: '#E5435E'},
                  },
                  '& .MuiSvgIcon-root': {
                    color:
                      productLine === FERTILITY
                        ? colors.primary
                        : PGTheme.productLines.menopause.primary,
                  },
                }}
              >
                <Box pt={1} pb={2}>
                  <AlertTitle sx={{color: theme.palette.text.primary}}>
                    {t('profile_personal_info_delete_account')}
                  </AlertTitle>
                  <Typography
                    variant={'caption'}
                    sx={{color: theme.palette.text.primary}}
                  >
                    {t('profile_personal_info_delete_account_help')}
                    <br />
                    <Link
                      href={`mailto:support@${REACT_APP_DOMAIN_NAME}`}
                      sx={{
                        color:
                          productLine === MENOPAUSE
                            ? colors.primary
                            : PGTheme.productLines.fertility.primary,
                      }}
                    >
                      support@{REACT_APP_DOMAIN_NAME}
                    </Link>
                  </Typography>
                </Box>
              </Alert>
            </Box>
          </Grid>
        </Grid>
      </ErrorBoundary>
    </Box>
  )
}

export {PersonalInfoPage}
